import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { updateStore } from '../lib/api';
import { toast } from 'sonner';

export default function EditStoreDialog({ store, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (store) setForm({
      name: store.name || '',
      description: store.description || '',
      phone: store.phone || '',
      address: store.address || '',
      pincode: store.pincode || '',
      village: store.village || '',
      delivery_fee: store.delivery_fee ?? 0,
      min_order: store.min_order ?? 0,
      established_year: store.established_year || '',
      languages: (store.languages || []).join(', '),
      return_policy: store.return_policy || '',
      hours: store.hours || '',
      accessibility_notes: store.accessibility_notes || '',
    });
  }, [store]);

  const save = async () => {
    setBusy(true);
    try {
      if (form.pincode && !/^[1-9][0-9]{5}$/.test(String(form.pincode).trim())) {
        toast.error('PIN must be a valid 6-digit Indian pincode (100000-999999)');
        setBusy(false); return;
      }
      if (form.phone && !/^[6-9][0-9]{9}$/.test(String(form.phone).replace(/\D/g, ''))) {
        toast.error('Phone must be a 10-digit Indian mobile number starting with 6-9');
        setBusy(false); return;
      }
      const payload = {
        ...form,
        pincode: form.pincode ? String(form.pincode).trim() : '',
        village: form.village ? String(form.village).trim() : '',
        delivery_fee: parseFloat(form.delivery_fee) || 0,
        min_order: parseFloat(form.min_order) || 0,
        established_year: form.established_year ? parseInt(form.established_year) : null,
        languages: form.languages.split(',').map(s => s.trim()).filter(Boolean),
      };
      const updated = await updateStore(store.id, payload);
      toast.success('Store updated — buyers in your PIN can now find you!');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!store) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit store profile</DialogTitle>
          <DialogDescription>Update your store's trust info — customers see this on the comparison page.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          <Inp label="Store name" testId="es-name" value={form.name} onChange={v=>setForm({...form, name:v})}/>
          <Inp label="Phone (10-digit)" testId="es-phone" value={form.phone} onChange={v=>setForm({...form, phone:v})} placeholder="9876543210"/>
          <Inp label="PIN code (6-digit)" testId="es-pincode" value={form.pincode} onChange={v=>setForm({...form, pincode:v})} placeholder="500001"/>
          <Inp label="Village / Town" testId="es-village" value={form.village} onChange={v=>setForm({...form, village:v})} placeholder="Rampur"/>
          <div className="md:col-span-2"><Inp label="Address" testId="es-address" value={form.address} onChange={v=>setForm({...form, address:v})}/></div>
          <div className="md:col-span-2"><Inp label="Description" testId="es-desc" value={form.description} onChange={v=>setForm({...form, description:v})} textarea/></div>
          <Inp label="Established year" testId="es-year" value={form.established_year} onChange={v=>setForm({...form, established_year:v})}/>
          <Inp label="Languages (comma-separated)" testId="es-langs" value={form.languages} onChange={v=>setForm({...form, languages:v})} placeholder="Hindi, English"/>
          <Inp label="Opening hours" testId="es-hours" value={form.hours} onChange={v=>setForm({...form, hours:v})}/>
          <Inp label="Accessibility notes" testId="es-access" value={form.accessibility_notes} onChange={v=>setForm({...form, accessibility_notes:v})}/>
          <div className="md:col-span-2"><Inp label="Return policy" testId="es-return" value={form.return_policy} onChange={v=>setForm({...form, return_policy:v})} textarea/></div>
          <Inp label="Delivery fee ₹" testId="es-fee" value={form.delivery_fee} onChange={v=>setForm({...form, delivery_fee:v})}/>
          <Inp label="Minimum order ₹" testId="es-min" value={form.min_order} onChange={v=>setForm({...form, min_order:v})}/>
        </div>
        <button data-testid="save-store-button" disabled={busy} onClick={save} className="btn-primary w-full mt-4">{busy ? 'Saving…' : 'Save changes'}</button>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, testId, textarea, placeholder }) {
  const cls = "w-full border border-border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20";
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {textarea ? <textarea data-testid={testId} rows={3} value={value} onChange={e=>onChange(e.target.value)} className={cls} placeholder={placeholder}/>
        : <input data-testid={testId} value={value} onChange={e=>onChange(e.target.value)} className={cls} placeholder={placeholder}/>}
    </label>
  );
}
